/**
 * paliPathMap.ts
 *
 * This file provides a manual mapping from Romanized Pāḷi URL path segments
 * to their corresponding Kannada titles. This is necessary because the URL paths
 * use Pāḷi, while the jsonData keys and titles are in Kannada script.
 * This map ensures the Breadcrumbs component can correctly display the full
 * navigational path in Kannada.
 */
export const paliPathMap: Record<string, string> = {
  //============================
  // DĪGHA NIKĀYA (ದೀಘನಿಕಾಯ)
  //============================
  'dīghanikāya/sīlakkhandhavagga': 'ಸೀಲಕ್ಖನ್ಧವಗ್ಗ',
  'dīghanikāya/mahāvagga': 'ಮಹಾವಗ್ಗ',
  'dīghanikāya/pāthikavagga': 'ಪಾಥಿಕವಗ್ಗ',

  //================================
  // MAJJHIMA NIKĀYA (ಮಜ್ಝಿಮನಿಕಾಯ)
  //================================
  'majjhimanikāya/mūlapariyāyavagga': 'ಮೂಲಪರಿಯಾಯ ವರ್ಗ',
  'majjhimanikāya/sīhanādavagga': 'ಸಿಂಹನಾದ ವರ್ಗ',
  'majjhimanikāya/opammavagga': 'ಉಪಮಾನಗಳ ವರ್ಗ',
  'majjhimanikāya/mahāyamakavagga': 'ಮಹಾಯಮಕ ವರ್ಗ',
  'majjhimanikāya/cūḷayamakavagga': 'ಚೂಳಯಮಕ ವರ್ಗ',
  'majjhimanikāya/gahapativagga': 'ಗೃಹಪತಿ ವಗ್ಗ',
  'majjhimanikāya/bhikkhuvagga': 'ಭಿಕ್ಖು ವಗ್ಗ',
  'majjhimanikāya/paribbājakavagga': 'ಪರಿಬ್ಬಾಜಕ ವಗ್ಗ',
  'majjhimanikāya/rājavagga': 'ರಾಜವರ್ಗ',
  'majjhimanikāya/brāhmaṇavagga': 'ಬ್ರಾಹ್ಮಣ ವರ್ಗ',
  'majjhimanikāya/devadahavagga': 'ದೇವದಹ ವಗ್ಗ',
  'majjhimanikāya/anupadavagga': 'ಅನುಪದ ವಗ್ಗ',
  'majjhimanikāya/suññatavagga': 'ಸುಞ್ಞತ ವಗ್ಗ',
  'majjhimanikāya/vibhaṅgavagga': 'ವಿಭಂಗ ವಗ್ಗ',
  'majjhimanikāya/saḷāyatanavagga': 'ಸಳಾಯತನ ವಗ್ಗ',

  //==================================
  // SAṂYUTTA NIKĀYA (ಸಂಯುತ್ತನಿಕಾಯ)
  //==================================

  // --- 1. Sagāthāvagga (ಸಗಾಥಾವಗ್ಗ) ---
  'saṃyuttanikāya/1.sagāthāvagga': 'ಸಗಾಥಾವಗ್ಗ',

  // 1.1 Devatā Saṃyutta (ದೇವತಾ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ': 'ದೇವತಾ ಸಂಯುಕ್ತ (SN 1)',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/1.naḷavaggo': 'ನಳವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/2.nandanavaggo': 'ನಂದನವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/3.sattivaggo': 'ಸತ್ತಿವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/4.satullapakāyikavaggo': 'ಸತುಲ್ಲಪಕಾಯಿಕವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/5.ādittavaggo': 'ಆದಿತ್ತವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/6.jarāvaggo': 'ಜರಾವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/7.addhavaggo': 'ಅದ್ಧವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/1.devatāsaṃyuttaṃ/8.chetvāvaggo': 'ಛೇತ್ವಾವಗ್ಗ',

  // 1.2 Devaputta Saṃyutta (ದೇವಪುತ್ರ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/2.devaputtasaṃyuttaṃ': 'ದೇವಪುತ್ರ ಸಂಯುಕ್ತ (SN 2)',
  'saṃyuttanikāya/1.sagāthāvagga/2.devaputtasaṃyuttaṃ/1.paṭhamavaggo': 'ಪಠಮವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/2.devaputtasaṃyuttaṃ/2.anāthapiṇḍikavaggo': 'ಅನಾಥಪಿಂಡಿಕವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/2.devaputtasaṃyuttaṃ/3.nānātitthiyavaggo': 'ನಾನಾತಿತ್ಥಿಯವಗ್ಗ',

  // 1.3 Kosala Saṃyutta (ಕೋಸಲ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/3.kosalasaṃyuttaṃ': 'ಕೋಸಲ ಸಂಯುಕ್ತ (SN 3)',
  'saṃyuttanikāya/1.sagāthāvagga/3.kosalasaṃyuttaṃ/1.paṭhamavaggo': 'ಪಠಮವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/3.kosalasaṃyuttaṃ/2.dutiyavaggo': 'ದುತಿಯವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/3.kosalasaṃyuttaṃ/3.tatiyavaggo': 'ತತಿಯವಗ್ಗ',

  // 1.4 Māra Saṃyutta (ಮಾರ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/4.mārasaṃyuttaṃ': 'ಮಾರ ಸಂಯುಕ್ತ (SN 4)',
  'saṃyuttanikāya/1.sagāthāvagga/4.mārasaṃyuttaṃ/1.paṭhamavaggo': 'ಪಠಮವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/4.mārasaṃyuttaṃ/2.dutiyavaggo': 'ದುತಿಯವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/4.mārasaṃyuttaṃ/3.tatiyavaggo': 'ತತಿಯವಗ್ಗ',

  // 1.5 Bhikkhunī Saṃyutta (ಭಿಕ್ಖುನೀ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/5.bhikkhunīsaṃyuttaṃ': 'ಭಿಕ್ಖುನೀ ಸಂಯುಕ್ತ (SN 5)',

  // 1.6 Brahma Saṃyutta (ಬ್ರಹ್ಮ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/6.brahmasaṃyuttaṃ': 'ಬ್ರಹ್ಮ ಸಂಯುಕ್ತ (SN 6)',
  'saṃyuttanikāya/1.sagāthāvagga/6.brahmasaṃyuttaṃ/1.paṭhamavaggo': 'ಪಠಮವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/6.brahmasaṃyuttaṃ/2.dutiyavaggo': 'ದುತಿಯವಗ್ಗ',

  // 1.7 Brāhmaṇa Saṃyutta (ಬ್ರಾಹ್ಮಣ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/7.brāhmaṇasaṃyuttaṃ': 'ಬ್ರಾಹ್ಮಣ ಸಂಯುಕ್ತ (SN 7)',
  'saṃyuttanikāya/1.sagāthāvagga/7.brāhmaṇasaṃyuttaṃ/1.arahantavaggo': 'ಅರಹಂತವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/7.brāhmaṇasaṃyuttaṃ/2.upāsakavaggo': 'ಉಪಾಸಕವಗ್ಗ',

  // 1.8 Vaṅgīsa Saṃyutta (ವಂಗೀಸ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/8.vaṅgīsasaṃyuttaṃ': 'ವಂಗೀಸ ಸಂಯುಕ್ತ (SN 8)',

  // 1.9 Vana Saṃyutta (ವನ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/9.vanasaṃyuttaṃ': 'ವನ ಸಂಯುಕ್ತ (SN 9)',

  // 1.10 Yakkha Saṃyutta (ಯಕ್ಖ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/10.yakkhasaṃyuttaṃ': 'ಯಕ್ಖ ಸಂಯುಕ್ತ (SN 10)',

  // 1.11 Sakka Saṃyutta (ಸಕ್ಕ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/1.sagāthāvagga/11.sakkasaṃyuttaṃ': 'ಸಕ್ಕ ಸಂಯುಕ್ತ (SN 11)',
  'saṃyuttanikāya/1.sagāthāvagga/11.sakkasaṃyuttaṃ/1. Paṭhamavaggo': 'ಪ್ರಥಮ ವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/11.sakkasaṃyuttaṃ/2. Dutiyavaggo': 'ದ್ವಿತೀಯ ವಗ್ಗ',
  'saṃyuttanikāya/1.sagāthāvagga/11.sakkasaṃyuttaṃ/3. Tatiyavaggo': 'ತೃತೀಯ ವಗ್ಗ',

  // --- 4. Saḷāyatanavagga (ಸಳಾಯತನವಗ್ಗೋ) ---
  'saṃyuttanikāya/4.saḷāyatanavagga': 'ಸಳಾಯತನವಗ್ಗೋ (ಷಡ್ ಆಯಾತನ ವರ್ಗ - SN 4)',

  // 4.1 Saḷāyatana Saṃyutta (ಸಳಾಯತನ ಸಂಯುಕ್ತ)
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ': 'ಸಳಾಯತನ ಸಂಯುಕ್ತ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/1.aniccavaggo': 'ಅನಿಚ್ಚವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/2.yamakavaggo': 'ಯಮಕವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/3.sabbavaggo': 'ಸಬ್ಬವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/4.jātidhammavaggo': 'ಜಾತಿಧಮ್ಮವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/5.sabbaaniccavaggo': 'ಸಬ್ಬಾನಿಚ್ಚವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/6.avijjāvaggo': 'ಅವಿಜ್ಜಾವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/7.migajālavaggo': 'ಮಿಗಜಾಲವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/8.gilānavaggo': 'ಗಿಲಾನವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/9.channavaggo': 'ಛನ್ನವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/10.saḷavaggo': 'ಸಳವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/11.Yogakkhemivaggo': 'ಯೋಗಕ್ಖೇಮಿವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/12.lokakamagunavaggo': 'ಲೋಕಕಾಮಗುಣ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/13.gahapativaggo': 'ಗಹಪತಿ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/14.devadahavaggo': 'ದೇವದಹ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/15.navapurāṇavaggo': 'ನವಪುರಾಣ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/16.nandikkhayavaggo': 'ನಂದಿಕ್ಖಯ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/17.saṭṭhipeyyālavaggo':
    'ಸಟ್ಠಿಪೇಯ್ಯಾಲ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/18.samuddavaggo': 'ಸಮುದ್ದ ವಗ್ಗ',
  'saṃyuttanikāya/4.saḷāyatanavagga/1.saḷāyatanasamyuttaṃ/19.āsīvisavaggo': 'ಆಸೀವಿ ವಗ್ಗ',

  // Add any further mappings for other Nikāyas or Saṃyuttas here as needed.
};
